'use client';

import { useState } from 'react';

interface TrackingEmptyStateProps {
  reference: string;
  suggestedCarrier: string | null;
  suggestedCarrierStage: string | null;
  detectedCarrier: string | null;
}

export function TrackingEmptyState({
  reference,
  suggestedCarrier,
  suggestedCarrierStage,
  detectedCarrier,
}: TrackingEmptyStateProps) {
  const carrier = detectedCarrier ?? suggestedCarrier;
  const isUnconnectedCarrier = carrier && suggestedCarrierStage !== 'live';

  if (isUnconnectedCarrier) {
    return (
      <div className="tracking-empty-box">
        <div className="tracking-empty-heading">{carrier} tracking is not available yet</div>
        <p className="tracking-empty-message">
          We&apos;re working on adding {carrier} tracking.
          Leave your email and we&apos;ll notify you when it&apos;s available.
        </p>
        <EmailForm reference={reference} carrier={carrier} />
      </div>
    );
  }

  return (
    <div className="tracking-empty-box">
      <div className="tracking-empty-heading">No tracking events found</div>
      <p className="tracking-empty-message">
        No results for <strong>{reference}</strong>. The reference may be incorrect,
        the shipment may not be registered yet, or the carrier isn&apos;t supported.
      </p>
      <p className="tracking-empty-message">
        Need tracking for a carrier we don&apos;t support yet? Leave your email and we&apos;ll reach out.
      </p>
      <EmailForm reference={reference} carrier={null} />
    </div>
  );
}

function EmailForm({ reference, carrier }: { reference: string; carrier: string | null }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setState('submitting');
    try {
      const res = await fetch('/api/tracking-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), reference, carrier }),
      });
      if (res.ok) setState('success');
      else setState('error');
    } catch {
      setState('error');
    }
  }

  if (state === 'success') {
    return <div className="tracking-empty-success">Thanks! We&apos;ll be in touch.</div>;
  }

  return (
    <form className="tracking-empty-form" onSubmit={handleSubmit}>
      <input
        className="tracking-empty-input"
        type="email"
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={state === 'submitting'}
        required
      />
      <button
        className="tracking-empty-submit"
        type="submit"
        disabled={state === 'submitting' || !email.trim()}
      >
        {state === 'submitting' ? 'Sending…' : 'Notify Me'}
      </button>
      {state === 'error' && (
        <div className="tracking-empty-error">Something went wrong. Please try again.</div>
      )}
    </form>
  );
}
